import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Badge, Spinner, Alert, Table, Accordion, Form,
} from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { API_URL } from '../api';
import { useGeolocation, getSavedRadius } from '../hooks/useGeolocation';

// Leaflet icon setup
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

const userIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33],
  className: 'leaflet-marker-user',
});

function numberedIcon(n: number): L.DivIcon {
  return L.divIcon({
    html: `<div style="background:#0d6efd;color:white;border-radius:50%;width:28px;height:28px;
      display:flex;align-items:center;justify-content:center;font-weight:bold;
      border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);">${n}</div>`,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

// --- Types ---

interface PlanItem {
  product_id: number;
  product_name: string;
  price: number;
  is_on_offer: boolean;
}

interface PlanStore {
  store_id: number;
  store_name: string;
  chain_name: string;
  distance_m: number;
  store_location: { type: string; coordinates: [number, number] };
  items: PlanItem[];
  store_total: number;
  navigation_url: string | null;
}

interface ShoppingPlan {
  plan_id: string;
  kind: 'single' | 'split';
  label: string;
  total_price: number;
  items_found: number;
  items_total: number;
  stores: PlanStore[];
  full_route_navigation_url: string | null;
}

interface PlansResult {
  list_id: string;
  list_name: string;
  items_total: number;
  plans: ShoppingPlan[];
}

const formatPrice = (n: number) => `${n.toFixed(2)} €`;
const formatDist = (m: number) =>
  m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;

const SETTINGS_KEY = 'smartcart_plan_settings';

interface PlanSettings {
  include_single_store: boolean;
  include_partial: boolean;
}

function loadSettings(): PlanSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { include_single_store: true, include_partial: false };
}

function saveSettings(s: PlanSettings) {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

// --- Component ---

const BasketCompare: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const { location, source: locationSource, error: geoError, loading: geoLoading } = useGeolocation();
  const RADIUS = getSavedRadius();

  const [settings, setSettings] = useState<PlanSettings>(loadSettings);
  const [data, setData] = useState<PlansResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    if (!listId || !location) return;
    const params = new URLSearchParams({
      lat: location.lat.toString(),
      lng: location.lng.toString(),
      radius_m: RADIUS.toString(),
      include_single_store: String(settings.include_single_store),
      include_partial: String(settings.include_partial),
      max_plans: '6',
    });
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/shopping_lists/${listId}/plans?${params}`);
      if (!res.ok) throw new Error(`Fehler beim Laden (${res.status})`);
      const result: PlansResult = await res.json();
      setData(result);
      // Auto-select cheapest full-coverage plan, or first plan otherwise
      const firstFull = result.plans.find(p => p.items_found === p.items_total);
      setSelectedPlanId((firstFull ?? result.plans[0])?.plan_id ?? null);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [listId, location, RADIUS, settings]);

  useEffect(() => {
    if (location) fetchPlans();
  }, [location, fetchPlans]);

  const updateSettings = (patch: Partial<PlanSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveSettings(next);
  };

  const selectedPlan = useMemo(
    () => data?.plans.find(p => p.plan_id === selectedPlanId) ?? null,
    [data, selectedPlanId]
  );

  if (geoLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" size="sm" /> <span className="ms-2">Standort wird ermittelt...</span>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <Link to="/shopping-lists" className="btn btn-outline-secondary btn-sm me-3">&larr; Listen</Link>
        <h4 className="mb-0">{data?.list_name || 'Preisvergleich'}</h4>
      </div>

      {geoError && <Alert variant="warning" className="py-2">{geoError}</Alert>}
      {location && (
        <div className="text-muted small mb-2">
          Standort: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
          {locationSource === 'home' && ' (Home)'}
          {locationSource === 'gps' && ' (GPS)'}
          {locationSource === 'fallback' && ' (Wien)'}
          {' '}&middot; Radius: {RADIUS >= 1000 ? `${(RADIUS / 1000).toFixed(1)} km` : `${RADIUS} m`}
          {data && <> &middot; {data.items_total} Artikel auf der Liste</>}
        </div>
      )}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <Form.Check
              type="checkbox"
              id="opt-single"
              label="Einzel-Markt-Optionen anzeigen"
              checked={settings.include_single_store}
              onChange={e => updateSettings({ include_single_store: e.target.checked })}
            />
            <Form.Check
              type="checkbox"
              id="opt-partial"
              label="Auch unvollständige Pläne anzeigen"
              checked={settings.include_partial}
              onChange={e => updateSettings({ include_partial: e.target.checked })}
            />
          </div>
        </Card.Body>
      </Card>

      {loading && <LoadingSpinner />}

      {data && !loading && data.plans.length === 0 && (
        <Alert variant="info">
          Keine Pläne gefunden{!settings.include_partial && ' — vielleicht "Auch unvollständige Pläne" aktivieren?'}.
        </Alert>
      )}

      {data && data.plans.length > 0 && (
        <PlansList
          plans={data.plans}
          itemsTotal={data.items_total}
          selectedId={selectedPlanId}
          onSelect={setSelectedPlanId}
        />
      )}

      {selectedPlan && location && (
        <div className="row g-3 mt-2">
          <div className="col-lg-6">
            <Card>
              <Card.Body className="p-0">
                <div style={{ height: '420px' }}>
                  <PlanMap plan={selectedPlan} userLocation={location} />
                </div>
              </Card.Body>
            </Card>
          </div>
          <div className="col-lg-6">
            <PlanDetail plan={selectedPlan} />
          </div>
        </div>
      )}
    </>
  );
};

// --- Plans list ---

const PlansList: React.FC<{
  plans: ShoppingPlan[];
  itemsTotal: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}> = ({ plans, itemsTotal, selectedId, onSelect }) => {
  const cheapestFull = plans.find(p => p.items_found === p.items_total);
  return (
    <div className="d-flex flex-column gap-2 mb-3">
      {plans.map((p, idx) => {
        const isSelected = p.plan_id === selectedId;
        const isBest = cheapestFull && p.plan_id === cheapestFull.plan_id;
        const fullCoverage = p.items_found === p.items_total;
        return (
          <Card
            key={p.plan_id}
            className={isSelected ? 'border-primary border-2' : ''}
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(p.plan_id)}
          >
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <Badge bg="secondary" className="me-2">{idx + 1}</Badge>
                  <strong>{p.label}</strong>
                  <span className="text-muted ms-2">
                    {p.stores.length} {p.stores.length === 1 ? 'Markt' : 'Märkte'}
                  </span>
                  {isBest && <Badge bg="success" className="ms-2">Günstigster</Badge>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge
                    bg={fullCoverage ? 'success' : p.items_found === 0 ? 'danger' : 'warning'}
                    text={fullCoverage ? undefined : 'dark'}
                  >
                    {p.items_found} von {itemsTotal} Artikeln
                  </Badge>
                  <span className="fs-4 fw-bold">{formatPrice(p.total_price)}</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

// --- Plan detail (per-store accordion + nav) ---

const PlanDetail: React.FC<{ plan: ShoppingPlan }> = ({ plan }) => (
  <Card>
    <Card.Header className="py-2 d-flex justify-content-between align-items-center">
      <span>{plan.label}</span>
      {plan.full_route_navigation_url && (
        <a
          href={plan.full_route_navigation_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-primary"
        >
          Gesamtroute &rarr;
        </a>
      )}
    </Card.Header>
    <Accordion defaultActiveKey="0" alwaysOpen>
      {plan.stores.map((s, idx) => (
        <Accordion.Item key={s.store_id} eventKey={String(idx)}>
          <Accordion.Header>
            <div className="d-flex justify-content-between w-100 me-3 align-items-center">
              <span>
                {plan.stores.length > 1 && (
                  <Badge bg="primary" className="me-2">{idx + 1}</Badge>
                )}
                <strong>{s.chain_name}</strong>
                <small className="text-muted ms-1">{s.store_name} &middot; {formatDist(s.distance_m)}</small>
              </span>
              <span className="fw-bold">{formatPrice(s.store_total)}</span>
            </div>
          </Accordion.Header>
          <Accordion.Body className="p-0">
            <Table size="sm" className="mb-0">
              <tbody>
                {s.items.map(item => (
                  <tr key={item.product_id}>
                    <td className="ps-3">
                      {item.product_name}
                      {item.is_on_offer && (
                        <Badge bg="danger" className="ms-2" style={{ fontSize: '0.65em' }}>Aktion</Badge>
                      )}
                    </td>
                    <td className="text-end pe-3">{formatPrice(item.price)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {s.navigation_url && (
              <div className="p-2 text-end">
                <a
                  href={s.navigation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  Navigation
                </a>
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  </Card>
);

// --- Map ---

const PlanMap: React.FC<{
  plan: ShoppingPlan;
  userLocation: { lat: number; lng: number };
}> = ({ plan, userLocation }) => (
  <MapContainer
    center={[userLocation.lat, userLocation.lng]}
    zoom={13}
    style={{ height: '100%', width: '100%' }}
  >
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    />
    <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
      <Popup>Dein Standort</Popup>
    </Marker>
    {plan.stores.map((s, idx) => (
      <Marker
        key={s.store_id}
        position={[s.store_location.coordinates[1], s.store_location.coordinates[0]]}
        icon={plan.stores.length > 1 ? numberedIcon(idx + 1) : defaultIcon}
      >
        <Popup>
          <strong>{s.chain_name}</strong> &middot; {s.store_name}<br />
          {s.items.length} Artikel &middot; {formatPrice(s.store_total)}
        </Popup>
      </Marker>
    ))}
  </MapContainer>
);

const LoadingSpinner = () => (
  <div className="text-center my-4">
    <Spinner animation="border" size="sm" /> <span className="ms-2">Pläne werden berechnet...</span>
  </div>
);

export default BasketCompare;
